/**
 * Trending revalidation.
 *
 * FeedContext keeps a per-tab cache and skips the network when a tab it has
 * already loaded is revisited, which is right for the stable feeds but leaves
 * Trending showing whatever it ranked the first time. Trending is a live
 * ranking, so entering it — on mount or on a tab switch — refetches and
 * re-ranks in the background while the cached list stays on screen.
 */
import { useEffect, useRef } from 'react';
import { useFeed } from '@wlb/shared/context/FeedContext';
import type { FeedTab } from '@wlb/shared/types';

/**
 * True when the viewer has just entered Trending. `null` is the first render,
 * so a mount straight onto Trending revalidates too.
 */
export const shouldRevalidateTrending = (
  previousTab: FeedTab | null,
  activeTab: FeedTab,
): boolean => activeTab === 'trending' && previousTab !== 'trending';

export const useTrendingRevalidation = (
  activeTab: FeedTab,
  enabled: boolean,
): void => {
  const { refreshFeed } = useFeed();
  const previousTabRef = useRef<FeedTab | null>(null);

  useEffect(() => {
    if (!enabled) {
      // Signing out clears the feed; the next entry starts fresh.
      previousTabRef.current = null;
      return;
    }

    const previousTab = previousTabRef.current;
    previousTabRef.current = activeTab;
    if (!shouldRevalidateTrending(previousTab, activeTab)) return;

    refreshFeed().catch(() => undefined);
  }, [activeTab, enabled, refreshFeed]);
};
